// The company's list of what its machines ARE (round 28).
//
// Competency codes' sibling, and deliberately its clone: a manager-curated
// name list living under Resources, two tabs (an archived type is not a
// lesser active one — it must not be offered on new machines but still names
// the old ones), archive-first with delete only for the never-used.
//
// The list exists for the forms module: an asset form's "Offered machines"
// filter references these BY ID, so renaming one here renames it on every
// template filter at once — which is the whole reason it is a list and not
// free text.
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api';
import { machineTypes, type MachineType } from '../fleetApi';
import { Banner, Field } from '../ui';
import { ActionMenu, Modal, PageHeader, useConfirm } from '../components/ui';
import { useTabParam } from '../lib/useTabParam';

const TABS = ['active', 'archived'] as const;

export function MachineTypesPage() {
  const confirm = useConfirm();
  const [types, setTypes] = useState<MachineType[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<MachineType | 'new' | null>(null);
  const [tab, setTab] = useTabParam(TABS, 'active');

  const load = useCallback(async () => {
    try {
      const result = await machineTypes.list({ includeArchived: true });
      setTypes(result.machineTypes);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the machine types.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const all = types ?? [];
  const active = all.filter((t) => !t.archived);
  const archived = all.filter((t) => t.archived);
  const showing = tab === 'archived' ? archived : active;

  async function setArchived(type: MachineType, archive: boolean) {
    try {
      await machineTypes.archive(type.id, archive);
      setNotice(archive ? `${type.name} archived.` : `${type.name} restored.`);
      setError(null);
      setTab(archive ? 'archived' : 'active');
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not do that.');
    }
  }

  async function remove(type: MachineType) {
    const ok = await confirm({
      title: `Delete ${type.name}?`,
      message:
        'If any machine has this type the delete is refused, and you can archive it instead; ' +
        'archiving keeps it on the machines and form filters that hold it and stops it being ' +
        'offered on new ones.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await machineTypes.remove(type.id);
      setNotice(`${type.name} deleted.`);
      setError(null);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that.');
      setNotice(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Machine types"
        subtitle="What your machines are: excavators, tippers, rollers. Used to say which machines a form is for."
        actions={
          <button type="button" className="btn-primary" onClick={() => setEditing('new')}>
            Add type
          </button>
        }
      />

      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            aria-current={tab === key ? 'page' : undefined}
            className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
              tab === key
                ? 'border-brand-700 font-medium text-brand-800'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {key === 'active' ? 'Active' : 'Archived'}
            <span className="ml-1 text-xs text-gray-500">
              {key === 'active' ? active.length : archived.length}
            </span>
          </button>
        ))}
      </div>

      <section className="panel">
        {types === null ? (
          <p className="muted">Loading…</p>
        ) : showing.length === 0 ? (
          <p className="muted">
            {tab === 'archived'
              ? 'Nothing archived. Archiving a type keeps it on the machines that hold it and stops it being offered on new ones.'
              : 'No machine types yet. Add what your fleet is made of (excavators, tippers, water carts) and set each machine’s type on its own page.'}
          </p>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0">
            {showing.map((type) => (
              <li
                key={type.id}
                className="flex items-start justify-between gap-3 border-t border-gray-200 pt-2 first:border-0 first:pt-0"
              >
                <span className="font-medium">{type.name}</span>
                <ActionMenu
                  items={[
                    { label: 'Rename', onClick: () => setEditing(type) },
                    type.archived
                      ? { label: 'Restore', onClick: () => void setArchived(type, false) }
                      : { label: 'Archive', onClick: () => void setArchived(type, true) },
                    { label: 'Delete', tone: 'danger', onClick: () => void remove(type) },
                  ]}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <TypeModal
          machineType={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            setNotice(message);
            setError(null);
            void load();
          }}
        />
      )}
    </>
  );
}

function TypeModal({
  machineType,
  onClose,
  onSaved,
}: {
  machineType: MachineType | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [name, setName] = useState(machineType?.name ?? '');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});

    try {
      if (machineType) {
        await machineTypes.update(machineType.id, { name: name.trim() });
        onSaved('Saved. The new name shows everywhere the type is used.');
      } else {
        await machineTypes.create({ name: name.trim() });
        onSaved(`${name.trim()} added.`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not save that.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={machineType ? `Rename ${machineType.name}` : 'Add machine type'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="machine-type-form" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="machine-type-form" onSubmit={save} noValidate>
        {error && <Banner kind="error">{error}</Banner>}

        <Field
          label="Name"
          name="name"
          value={name}
          onChange={setName}
          error={fields.name}
          hint="Renaming updates every machine and every form filter that uses it."
        />
      </form>
    </Modal>
  );
}
